/**
 * AI budget — a Configuration tab, admins only. The monthly cap every AI
 * call is gated against, the warning threshold, and whether reaching the cap
 * refuses new calls. It is set for the current month; later months inherit
 * it until changed. Two columns: this month's spend as a meter that links to
 * the AI usage page, and the fields stacked beside it.
 */

import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { BudgetIn, budgetInAlertThresholdPctMax, budgetInLimitUsdOneMax, budgetInLimitUsdOneMin } from '@/api/generated/schemas';
import { useBudget, useSaveBudget } from '@/api/queries';
import type { Budget } from '@/api/types';
import { bannerError, fieldErrors, serverFieldErrors, type FieldErrors } from '@/api/validate';
import { FieldError, fieldA11y } from '@/ui/FieldError';
import { formatPct, formatUsd, monthLabel } from '@/ui/format';
import { FormError, LoadFailure, LoadingCard } from '@/ui/StateCard';
import { buttonState } from '@/ui/ActionButton';
import { InfoTip } from '@/ui/FieldLabel';
import { SaveBar } from '@/ui/SaveBar';
import { useSession } from '@/session/SessionContext';

import { useBudgetForm, type BudgetFormState } from './useBudgetForm';

// The generator names no constant for a minimum of 1; the schema still carries it.
const THRESHOLD_MIN = BudgetIn.shape.alert_threshold_pct.unwrap().minValue ?? 1;

/** The typed fields by API path, with their inputs' ids: each shows its problem beside it, the banner the rest. */
const INPUT_IDS = { limit_usd: 'budget-limit', alert_threshold_pct: 'budget-threshold' } as const satisfies Partial<Record<keyof typeof BudgetIn.shape, string>>;
const PLACED = Object.keys(INPUT_IDS);
const MESSAGES = {
  limit_usd: `From ${formatUsd(budgetInLimitUsdOneMin)} to ${formatUsd(budgetInLimitUsdOneMax)}.`,
  alert_threshold_pct: `A whole number from ${THRESHOLD_MIN} to ${budgetInAlertThresholdPctMax}.`,
};

/** The cap as the API checks it: a number once it reads as one (the field holds text). */
const asAmount = (v: string) => (v.trim() === '' || Number.isNaN(Number(v)) ? v : Number(v));

export function BudgetTab() {
  const budget = useBudget();
  if (budget.isPending) return <LoadingCard label="Loading the AI budget" />;
  if (budget.error) return <LoadFailure error={budget.error} onRetry={() => void budget.refetch()} />;
  return <BudgetForm budget={budget.data} />;
}

function BudgetForm({ budget }: { budget: Budget }) {
  const save = useSaveBudget();
  const editable = useSession().can('ai.budget.manage');
  const form = useBudgetForm(budget);
  const body = { limit_usd: form.limit, hard_stop: form.hardStop, alert_threshold_pct: form.threshold };
  const client = fieldErrors(BudgetIn, { ...body, limit_usd: asAmount(form.limit) }, MESSAGES);
  const invalid = Object.keys(client).length > 0;

  return (
    <div className="card">
      <form
        className="card-body stack-md"
        onSubmit={(event) => {
          event.preventDefault();
          if (!invalid) save.mutate(body);
        }}
      >
        <p className="hint">
          It applies from {monthLabel(budget.month)}; later months inherit it until changed.
        </p>
        <FormError error={bannerError(save.error, PLACED)} />
        <div className="grid grid-2 budget-layout">
          <BudgetMeter budget={budget} />
          <div className="stack-md">
            <BudgetFields form={form} editable={editable} errors={{ ...serverFieldErrors(save.error), ...client }} />
            {editable ? (
              <SaveBar changed={form.changed} valid={!invalid} state={buttonState(save)} onReset={form.reset} />
            ) : (
              <p className="hint">You can view the budget but not change it.</p>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

function BudgetFields({ form, editable, errors }: { form: BudgetFormState; editable: boolean; errors: FieldErrors }) {
  return (
    <>
      <div className="field">
        <label htmlFor={INPUT_IDS.limit_usd}>Monthly cap (USD)</label>
        <div className="input-prefix">
          <span className="prefix" aria-hidden="true">$</span>
          <input
            id={INPUT_IDS.limit_usd}
            type="number"
            inputMode="decimal"
            min={budgetInLimitUsdOneMin}
            max={budgetInLimitUsdOneMax}
            step="0.01"
            required
            value={form.limit}
            readOnly={!editable}
            {...fieldA11y(INPUT_IDS.limit_usd, errors.limit_usd)}
            onChange={(e) => form.setLimit(e.target.value)}
          />
        </div>
        <FieldError inputId={INPUT_IDS.limit_usd} message={errors.limit_usd} />
      </div>
      <div className="field">
        <label htmlFor={INPUT_IDS.alert_threshold_pct}>Warn at</label>
        <div className="input-suffix">
          <input
            id={INPUT_IDS.alert_threshold_pct}
            type="number"
            inputMode="numeric"
            min={THRESHOLD_MIN}
            max={budgetInAlertThresholdPctMax}
            required
            value={form.threshold}
            readOnly={!editable}
            {...fieldA11y(INPUT_IDS.alert_threshold_pct, errors.alert_threshold_pct)}
            onChange={(e) => form.setThreshold(Number(e.target.value))}
          />
          <span className="suffix" aria-hidden="true">%</span>
        </div>
        <FieldError inputId={INPUT_IDS.alert_threshold_pct} message={errors.alert_threshold_pct} />
      </div>
      <div className="field">
        <span className="field-label">
          <span className="label" id="budget-hard-stop">Hard stop</span>
          <InfoTip id="budget-hard-stop-hint" about="Hard stop">
            When on, new AI calls are refused once this month's spend reaches the cap. When off, calls continue and only the warning shows.
          </InfoTip>
        </span>
        <label className="check check-row">
          <input type="checkbox" checked={form.hardStop} aria-describedby="budget-hard-stop budget-hard-stop-hint" disabled={!editable} onChange={(e) => form.setHardStop(e.target.checked)} />
          <span className="ck-body">Refuse new AI calls at the cap</span>
        </label>
      </div>
    </>
  );
}

/** This month's spend against the cap, with the warning threshold marked; links to AI usage. */
function BudgetMeter({ budget }: { budget: Budget }) {
  const used = Math.min(100, Math.max(0, budget.used_pct));
  const state = budget.used_pct >= 100 ? ' is-over' : budget.used_pct >= budget.alert_threshold_pct ? ' is-warn' : '';
  return (
    <Link to="/ai-usage" className="budget-meter" aria-label={`This month: ${formatUsd(budget.committed_usd)} spent, ${formatPct(budget.used_pct)} of the cap. Open AI usage`}>
      <span className="budget-meter-head">
        <span>
          This month <b>{formatUsd(budget.committed_usd)}</b> of {formatUsd(budget.limit_usd)}
        </span>
        <span className="budget-meter-pct">{formatPct(budget.used_pct)}</span>
      </span>
      <span className={`meter${state}`} aria-hidden="true">
        <span className="meter-fill" style={{ width: `${used}%` }} />
        <span className="budget-meter-mark" style={{ left: `${budget.alert_threshold_pct}%` }} title={`Warn at ${budget.alert_threshold_pct}%`} />
      </span>
      <span className="budget-meter-link">
        See AI usage <ArrowRight aria-hidden="true" />
      </span>
    </Link>
  );
}
